import React from "react"

import { getIsAdminModeEnabledFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"

import type { IconProp } from "@/theme"
import i18n from "@/translations/i18n"
import type { Href } from "expo-router"

function makeDashboardPage<T extends string>({
  getIsMatch,
  href,
  ...rest
}: {
  IconComponent: IconProp
  getIsMatch?: (pathname: string) => boolean
  href: Href
  id: T
  isAdminOnly?: boolean
  isDevOnly?: boolean
  text: string
}) {
  return {
    getIsMatch:
      getIsMatch ??
      ((pathname) => {
        const target = typeof href === "string" ? href : href.pathname
        return pathname.startsWith(target)
      }),
    href,
    ...rest,
  } as const
}

const DASHBOARD_PAGES = [
  makeDashboardPage({
    IconComponent: "StatusMap",
    getIsMatch: (pathname) => pathname === "/",
    href: "/",
    id: "statusMap",
    text: i18n.t("appbarLinks.home"),
  }),
  makeDashboardPage({
    IconComponent: "DeviceGeneric",
    href: "/devices",
    id: "devices",
    text: i18n.t("appbarLinks.devices"),
  }),
  makeDashboardPage({
    IconComponent: "DeviceConnection",
    href: "/device-connections",
    id: "deviceConnections",
    text: i18n.t("appbarLinks.deviceConnections"),
  }),
  makeDashboardPage({
    IconComponent: "Clock",
    href: "/more/schedules-list",
    id: "schedules",
    text: i18n.t("appbarLinks.schedules"),
  }),
  makeDashboardPage({
    IconComponent: "Field",
    href: "/more/fields",
    id: "fields",
    text: i18n.t("appbarLinks.fields"),
  }),
  makeDashboardPage({
    IconComponent: "Settings",
    getIsMatch: (pathname) => pathname === "/more",
    href: "/more",
    id: "more",
    text: i18n.t("appbarLinks.settings"),
  }),
] as const
export type AppbarPage = (typeof DASHBOARD_PAGES)[number]
export type AppbarPageId = AppbarPage["id"]

/**
 * Get the list of pages for the appbar
 */
export function useAppbarPages() {
  const isAdminModeEnabled = useRootSelector(getIsAdminModeEnabledFromState)

  return React.useMemo(() => {
    return DASHBOARD_PAGES.filter((page) => {
      if (page.isAdminOnly === true && !isAdminModeEnabled) {
        return false
      }
      if (page.isDevOnly === true && !__DEV__) {
        return false
      }
      return true
    })
  }, [isAdminModeEnabled])
}
